// filepath: apps/web/src/features/cabinet/RequiredUserParameters/useMissingCount.ts
//
// FR-UI-CABINET-3 — chrome-button "missing count" hook.
//
// Reads the same TanStack Query cache as useRequiredParameters
// (single source of truth — the chrome button and the page cannot
// disagree on the missing count) and returns the count of fields
// where `missing === true`. The chrome UserMenu renders a badge
// when count >= 1; hides the badge when count === 0.
//
// The hook is read-only — it does not trigger a refetch and does
// not subscribe to refetch-on-window-focus. The page's own useQuery
// handles the underlying fetch lifecycle; this hook is a thin
// selector on top of the same cache entry.
//
// Test-friendliness: when no QueryClient is in scope (e.g., the
// chrome chrome tests that wrap the UserMenu without a
// QueryClientProvider), the hook returns 0 — the chrome degrades
// gracefully to "no badge" without throwing. This mirrors the
// established TanStack pattern at apps/web/src/features/auth
// /AuthProvider.test.tsx (uses QueryClient only when needed).

import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { useTenant } from "@/tenant/TenantProvider";
import { requiredParametersKeys, readSubjectFromAuth } from "./queries";
import type { RequiredParametersResponse } from "./types";

export function useMissingCount(): number {
  const { tenantSlug } = useTenant();
  const auth = useAuth();
  // useQueryClient() throws "No QueryClient set" when no
  // QueryClientProvider is in scope. Catch the error so the chrome
  // gracefully degrades to "no badge" in tests / pre-bootstrap.
  let client;
  try {
    client = useQueryClient();
  } catch {
    return 0;
  }
  const subject = readSubjectFromAuth(auth.user?.profile);
  const cached = client.getQueryData<RequiredParametersResponse>(
    requiredParametersKeys.detail(tenantSlug, subject),
  );
  if (!cached) return 0;
  return cached.fields.filter((f) => f.missing).length;
}
